<!-- Renders a single project-item row inside the ProjectItemsView list. -->
<template>
  <div class="data-card__main">
    <div class="data-card__name">{{ item.item.name }}</div>
    <div v-if="item.conflicts?.length" class="flex items-center gap-1 text-xs text-yellow-400 mb-0.5">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" class="w-3 h-3 shrink-0">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
      {{ $t('ui.doubleBooked') }} {{ item.conflicts.map(c => c.project_name).join(', ') }}
    </div>
    <div class="data-card__meta">
      <span v-if="item.serial_number" class="text-xs text-[var(--color-muted)]">S/N: {{ item.serial_number }}</span>
      <span v-if="item.asset_tag" class="text-xs text-[var(--color-muted)]">{{ $t('ui.assetTagLabel') }} {{ item.asset_tag }}</span>
      <button
        v-if="item.is_container"
        class="inline-flex items-center gap-1 text-xs text-teal-400 bg-teal-950/40 border border-teal-800/50 rounded px-1.5 py-0.5 cursor-pointer hover:bg-teal-900/50 transition-colors"
        @click.stop="$emit('navigate', item)"
      :title="$t('projectItems.openContainer')"
      >
        <MdiPackageVariantClosed class="w-3 h-3" />
          {{ item.children_count ?? 0 }} {{ $t('projectItems.containedCount') }}
      </button>
      <template v-if="item.item?.item_type === 2 || item.item?.item_type === 3 || item.item?.item_type === 4 || item.unresolved">
        <BaseInput
          v-if="editingQtyId === item.id"
          :model-value="editingQtyVal"
          @update:model-value="$emit('update:editingQtyVal', $event)"
          @blur="$emit('save-qty', item)"
          @keydown.enter.prevent="$emit('save-qty', item)"
          @keydown.escape="$emit('cancel-qty')"
          class="w-14 px-1 py-0.5 bg-[var(--color-surface)] border border-blue-500 rounded text-[var(--color-text)] text-xs font-semibold outline-none [appearance:textfield]"
          type="number"
          min="1"
          autofocus
          density="compact"
          @click.stop
        />
        <button
          v-else
          class="bg-[var(--color-surface-2)] border border-dashed border-[var(--color-border)] rounded text-[var(--color-muted)] text-xs font-semibold px-1 py-0.5 cursor-pointer hover:border-blue-500 hover:text-blue-300 disabled:cursor-default disabled:opacity-50 transition-colors"
          :disabled="item._removing"
          @click.stop="$emit('edit-qty', item)"
          :title="$t('projectItems.editQuantity')"
        >×{{ item.planned_quantity }}</button>
      </template>
    </div>

    <!-- Pricing row -->
    <div class="flex items-center flex-wrap gap-1 mt-1.5 text-xs text-[var(--color-muted)]">
          <span class="opacity-60">{{ $t('projectItems.ratePerDay') }}</span>
      <template v-if="editingPriceId === item.id">
        <BaseInput
          :model-value="editingPriceVal"
          @update:model-value="$emit('update:editingPriceVal', $event)"
          @blur="$emit('save-price', item)"
          @keydown.enter.prevent="$emit('save-price', item)"
          @keydown.escape="$emit('cancel-price')"
          class="w-20 px-1 py-0.5 bg-[var(--color-surface)] border border-blue-500 rounded text-[var(--color-text)] text-xs font-semibold outline-none [appearance:textfield]"
          type="number"
          min="0"
          step="0.01"
          :placeholder="$t('common.default')"
          autofocus
          density="compact"
          @click.stop
        />
      </template>
      <button
        v-else
        class="bg-[var(--color-surface-2)] border border-dashed border-[var(--color-border)] rounded text-[var(--color-muted)] text-xs font-semibold px-1.5 py-0.5 cursor-pointer flex items-center gap-1 hover:border-blue-500 hover:text-blue-300 disabled:cursor-default disabled:opacity-50 transition-colors"
        :class="{ 'border-yellow-600 text-yellow-400 hover:border-yellow-500 hover:text-yellow-200': item.price_override != null }"
        :disabled="item._removing"
        @click.stop="$emit('edit-price', item)"
        :title="$t('projectItems.setCustomRate')"
      >
        <template v-if="item.price_override != null">
          {{ formatPrice(parseFloat(item.price_override)) }}
            <span class="text-[0.6rem] bg-yellow-900 text-yellow-200 rounded px-1 uppercase tracking-wide">{{ $t('projectItems.priceCustomBadge') }}</span>
        </template>
        <template v-else-if="effectiveRate != null">
          {{ formatPrice(effectiveRate) }}
        </template>
        <template v-else>—</template>
      </button>
      <template v-if="totalPrice != null">
        <span class="opacity-40">=</span>
        <span class="font-semibold" :class="totalAfterDiscount != null ? 'text-[var(--color-muted)] line-through' : 'text-[var(--color-text)] opacity-70'">
          {{ formatPrice(totalPrice) }}
        </span>
        <template v-if="totalAfterDiscount != null">
          <span class="text-green-400 font-bold">→ {{ formatPrice(totalAfterDiscount) }}</span>
        </template>
      </template>
    </div>
  </div>

  <div class="data-card__actions">
    <!-- Packed indicator -->
    <template v-if="item.item?.item_type === 2 || item.item?.item_type === 3 || item.item?.item_type === 4 || item.unresolved">
      <div
        class="flex items-center gap-1 text-xs font-bold"
        :class="(item.packed_quantity ?? 0) >= item.planned_quantity ? 'text-green-600' : (item.packed_quantity ?? 0) > 0 ? 'text-yellow-600' : 'text-[var(--color-muted)] opacity-60'"
      >
        {{ item.packed_quantity ?? 0 }} / {{ item.planned_quantity }} {{ $t('projectItems.packed') }}
      </div>
    </template>
    <template v-else>
        <div v-if="item.packed" class="flex items-center gap-1 text-xs font-bold text-green-600">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="w-3.5 h-3.5">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
        {{ $t('projectItems.packedStatus') }}
      </div>
    </template>

    <!-- Move to section -->
    <BaseSelect
      v-if="sections.length"
      :model-value="item.section ?? ''"
      class="text-xs bg-[var(--color-surface-2)] border border-[var(--color-border)] rounded px-1 py-0.5 text-[var(--color-muted)] outline-none cursor-pointer max-w-[7rem]"
      density="compact"
      :title="$t('projectItems.moveToSection')"
      :aria-label="$t('projectItems.moveToSection')"
      @update:model-value="$emit('move', item, $event === '' ? null : parseInt($event, 10))"
    >
      <option value="">{{ $t('projectItems.ungrouped') }}</option>
      <option v-for="s in sections" :key="s.id" :value="s.id">{{ s.name }}</option>
    </BaseSelect>

    <!-- Remove -->
    <button
      class="btn-icon danger"
      :disabled="item._removing"
      @click="$emit('remove', item)"
      :title="$t('projectItems.removeFromPlan')"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6l-1 14H6L5 6"/>
        <path d="M10 11v6M14 11v6"/>
        <path d="M9 6V4h6v2"/>
      </svg>
    </button>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import { useI18n } from 'vue-i18n'
import MdiPackageVariantClosed from '~icons/mdi/package-variant-closed'

const { t } = useI18n()

const props = defineProps({
  item:               { type: Object,  required: true },
  editingQtyId:       { type: [Number, String], default: null },
  editingQtyVal:      { type: String,  default: '' },
  editingPriceId:     { type: [Number, String], default: null },
  editingPriceVal:    { type: String,  default: '' },
  sections:           { type: Array,   default: () => [] },
  effectiveUsageDays: { type: [Number, String], default: null },
  discountPercentage: { type: [Number, String], default: null },
})

defineEmits([
  'edit-qty', 'save-qty', 'cancel-qty', 'update:editingQtyVal',
  'edit-price', 'save-price', 'cancel-price', 'update:editingPriceVal',
  'remove', 'move', 'navigate',
])

const effectiveRate = computed(() =>
  props.item.effective_daily_rate != null ? parseFloat(props.item.effective_daily_rate) : null
)

const totalPrice = computed(() => {
  const rate = effectiveRate.value
  const days = props.effectiveUsageDays != null ? parseFloat(props.effectiveUsageDays) : null
  if (rate == null || days == null) return null
  return rate * (props.item.planned_quantity ?? 1) * days
})

const totalAfterDiscount = computed(() => {
  const total = totalPrice.value
  const disc  = parseFloat(props.discountPercentage ?? 0)
  if (total == null || !disc) return null
  return total * (1 - disc / 100)
})

function formatPrice(value) {
  if (value == null) return null
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
</script>
